import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RotateCcw, Check } from 'lucide-react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface Shape {
  key: string;
  label: string;
  color: string;
  confidence: number;
  /** Traço estilizado em viewBox 0 0 100 100 — não é a fonte real do app, só uma forma reconhecível desenhada como um gesto contínuo. */
  path: string;
}

// O classificador real só reconhece dígitos e letras (modelo model_62c:
// 0-9, A-Z, a-z — ver FreePaintActivity.kt) — nada de formas livres como
// círculo. Confiança sempre ≥ 80, o mesmo CONFIDENCE_THRESHOLD do app antes
// de aceitar um resultado.
const SHAPES: Shape[] = [
  { key: 'a', label: 'Letra A', color: '#F44336', confidence: 91, path: 'M28,86 L50,16 L72,86 M37,58 L63,58' },
  { key: '4', label: 'Número 4', color: '#03A9F4', confidence: 89, path: 'M65,15 L20,62 L78,62 M65,15 L65,88' },
  { key: '7', label: 'Número 7', color: '#673AB7', confidence: 88, path: 'M27,24 L73,24 L44,86' },
];

// Mesmas cores do seletor de pincel do app real (btnBlue, btnGreen, btnYellow,
// btnOrange, btnRed, btnPurple em fragment_free_paint.xml).
const PALETTE = ['#03A9F4', '#4CAF50', '#FFEB3B', '#FF9800', '#F44336', '#673AB7'];

type Phase = 'draw' | 'loading' | 'result';

interface Frame {
  shapeIndex: number;
  phase: Phase;
  duration: number;
}

function buildFrames(): Frame[] {
  const frames: Frame[] = [];
  SHAPES.forEach((_, shapeIndex) => {
    frames.push({ shapeIndex, phase: 'draw', duration: 2600 });
    frames.push({ shapeIndex, phase: 'loading', duration: 1300 });
    frames.push({ shapeIndex, phase: 'result', duration: 2400 });
  });
  return frames;
}

/**
 * Simulação do fluxo real de reconhecimento do FreePaintActivity: o traço se
 * desenha sozinho na tela escura, some por um instante em "Carregando
 * desenho..." (texto literal do loadingText em fragment_free_paint.xml) e
 * volta com o resultado — o mesmo debounce de ~3s do PaintView antes de
 * classificar o gesto.
 */
export function AIDrawingLiveDemo() {
  const frames = useMemo(buildFrames, []);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [frameIndex, setFrameIndex] = useState(0);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const frame = frames[frameIndex];
    const timer = setTimeout(() => setFrameIndex((i) => (i + 1) % frames.length), frame.duration);
    return () => clearTimeout(timer);
  }, [frameIndex, frames, prefersReducedMotion]);

  // Reseta o traço (escondido) e libera a transição de "revelar" toda vez que
  // uma nova forma começa a fase de desenho.
  useEffect(() => {
    if (prefersReducedMotion || frames[frameIndex].phase !== 'draw') return;
    setDrawn(false);
    const raf = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(raf);
  }, [frameIndex, frames, prefersReducedMotion]);

  const current = prefersReducedMotion ? { shapeIndex: 0, phase: 'result' as Phase } : frames[frameIndex];
  const shape = SHAPES[current.shapeIndex];
  const isDrawn = prefersReducedMotion ? true : drawn;

  return (
    <div className="rounded-[2rem] overflow-hidden bg-card p-5" aria-hidden="true">
      {/* Barra de ferramentas — voltar, tamanhos de pincel, desfazer (topo do fragment_free_paint.xml) */}
      <div className="flex items-center justify-between px-1 mb-4">
        <span className="w-8 h-8 rounded-full bg-foreground/10 flex items-center justify-center flex-shrink-0">
          <ArrowLeft className="w-4 h-4 text-foreground/60" />
        </span>
        <span className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-foreground/30" />
          <span className="w-2.5 h-2.5 rounded-full bg-foreground/30" />
          <span className="w-3.5 h-3.5 rounded-full bg-foreground/30" />
        </span>
        <span className="w-8 h-8 rounded-full bg-foreground/10 flex items-center justify-center flex-shrink-0">
          <RotateCcw className="w-4 h-4 text-foreground/60" />
        </span>
      </div>

      {/* Tela de desenho */}
      <div className="relative rounded-2xl bg-white aspect-[4/3] overflow-hidden">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          <path
            d={shape.path}
            fill="none"
            stroke={shape.color}
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            strokeDasharray={1}
            style={{
              strokeDashoffset: isDrawn ? 0 : 1,
              transition: current.phase === 'draw' ? 'stroke-dashoffset 2400ms linear' : 'none',
            }}
          />
        </svg>

        {current.phase === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/85">
            <span className="text-sm font-semibold text-muted-foreground animate-pulse">
              Carregando desenho...
            </span>
          </div>
        )}
      </div>

      {/* Resultado do reconhecimento */}
      <div className="mt-4 h-10 flex items-center justify-center">
        {current.phase === 'result' && (
          <div
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-white animate-scale-in"
            style={{ backgroundColor: shape.color }}
          >
            <Check className="w-4 h-4 flex-shrink-0" />
            Reconhecido: {shape.label} · {shape.confidence}%
          </div>
        )}
      </div>

      {/* Paleta de cores — mesmas cores do seletor real, a ativa em destaque */}
      <div className="flex items-center justify-center gap-2.5">
        {PALETTE.map((color) => (
          <span
            key={color}
            className="w-6 h-6 rounded-full transition-shadow duration-300"
            style={{
              backgroundColor: color,
              boxShadow:
                color === shape.color ? `0 0 0 2.5px rgb(var(--card)), 0 0 0 4px ${color}` : 'none',
            }}
          />
        ))}
      </div>
    </div>
  );
}
